import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '@/i18n/config';

/**
 * The language row at the foot of the auth screens on a phone (AL-1792).
 *
 * The layout only puts it on the small screens — on the desktop artboard there
 * is no language control at all, so this stays out of the way there.
 *
 * The list comes from the i18n config rather than being restated here, so a
 * language added there shows up without anyone remembering this file. The track
 * scrolls sideways for the same reason: four codes fit a phone comfortably, a
 * sixth would not, and the row should not start wrapping or squeezing the day
 * that happens.
 */
const LABELS: Record<string, string> = {
  en: 'EN',
  'pt-PT': 'PT',
  'pt-BR': 'BR',
  es: 'ES',
};

/** A language with no label yet still gets a sensible one, not a blank button. */
const labelFor = (code: string) => LABELS[code] ?? code.toUpperCase();

export const AuthLanguageSwitcher = () => {
  const { i18n } = useTranslation();

  return (
    <div className="no-scrollbar w-full overflow-x-auto">
      {/* w-max + mx-auto: centred while the row fits, scrollable once it does
          not. A centred flex container instead would clip the first item. */}
      <ul className="mx-auto flex w-max items-center gap-2 px-4 text-[11px] leading-4">
        {SUPPORTED_LANGUAGES.map((code, index) => (
          <li key={code} className="flex shrink-0 items-center gap-2">
            {index > 0 ? (
              <span aria-hidden="true" className="text-auth-body/40">
                /
              </span>
            ) : null}

            <button
              type="button"
              lang={code}
              aria-current={i18n.language === code ? 'true' : undefined}
              onClick={() => i18n.changeLanguage(code)}
              className={
                i18n.language === code
                  ? 'font-semibold text-auth-accent'
                  : 'text-auth-body hover:text-auth-heading'
              }
            >
              {labelFor(code)}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
